import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../App';
import { AlertTriangle, Download, Eye, Info, MapPin, ShieldCheck, Trash2 } from 'lucide-react';
import { datumUndZeit } from '../lib/zeit';

/**
 * DRIVER-DISPATCH-ONLINE-RADAR-001 (C) — „Fahrer-Radar – Prüfhinweise".
 *
 * Der Reiter ist eine reine PRÜF-Hilfe. Er sperrt nicht, schaltet nicht offline, ändert weder
 * Geld noch Kontostatus und behauptet niemals Schuld. Die Liste zeigt bewusst KEINE Kundennamen,
 * -nummern oder -adressen, keine Koordinaten und keinen unbereinigten Freitext; die exakte
 * Position bleibt ausschließlich über den bestehenden auditierten Standort-Reveal erreichbar.
 */

const SEVERITY = {
  rot:         { label: 'Roter Prüfhinweis', color: '#dc2626', bg: '#fef2f2' },
  beobachten:  { label: 'Beobachten',        color: '#d97706', bg: '#fffbeb' },
  gelb:        { label: 'Gelber Prüfhinweis', color: '#ca8a04', bg: '#fefce8' },
};
const REVIEW_STATUSES = [
  { key: 'unauffaellig', label: 'Unauffällig' },
  { key: 'beobachten',   label: 'Beobachten' },
  { key: 'erklaert',     label: 'Erklärt' },
  { key: 'bestaetigt',   label: 'Bestätigt' },
];
const fmt = (v) => datumUndZeit(v, { jahr: 'voll', sekunden: true, leer: '–' });

function SeverityPill({ value }) {
  const s = SEVERITY[value] || { label: value || '–', color: '#4b5563', bg: '#f1f5f9' };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
      padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{s.label}</span>
  );
}

function CaseRow({ c }) {
  return (
    <tr>
      <td>{c.ride_id}</td>
      <td>{c.reason_label}</td>
      <td>{fmt(c.arrived_at)}</td>
      <td>{fmt(c.released_at)}</td>
      <td>{c.pin_verified}</td>
      <td>{c.location_evidence}</td>
      <td>
        {c.ride_outcome_now}
        {c.ride_outcome_is_current_state
          ? <span style={{ color: '#4b5563', fontSize: 12, marginLeft: 6 }}>(aktueller Zustand)</span>
          : null}
      </td>
      <td>{c.incomplete_legacy
        ? <span style={{ color: '#d97706', fontSize: 12 }}>unvollständig (Legacy)</span>
        : '–'}</td>
    </tr>
  );
}

export default function DriverRadar() {
  const { can } = useAuth();
  const canManage = can && can('driver_radar.manage');
  const [rows, setRows] = useState([]);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState({ status: '', note: '' });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/admin/driver-radar?days=${windowDays}`);
      const drivers = r.data.drivers || [];
      setRows(drivers);
      return drivers;
    } catch (e) {
      setError(e?.response?.data?.error || 'Laden fehlgeschlagen');
      return null;
    } finally { setLoading(false); }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const openDriver = async (d) => {
    setOpen(d); setMsg(null);
    setForm({ status: d.review_status || '', note: '' });
    // R1/C4: Der Entscheidungsverlauf enthält die vollen Begründungen und die Entscheider —
    // er wird nur mit `driver_radar.manage` geladen. Reine Leser bekommen ihn gar nicht erst.
    if (!canManage) { setAudit([]); return; }
    try {
      const r = await api.get(`/admin/driver-radar/${d.driver_id}/audit`);
      setAudit(r.data.audit || []);
    } catch (e) { setAudit([]); }
  };

  const saveReview = async () => {
    if (!open) return;
    setMsg(null);
    try {
      const saved = await api.post(`/admin/driver-radar/${open.driver_id}/review`, {
        status: form.status, note: form.note, expected_revision: open.review_revision || 0,
      });
      setMsg({ ok: true, text: 'Prüfentscheid gespeichert.' });
      // R1/C3: Die vom Server bestätigte NEUE Revision sofort übernehmen. Ohne das wäre jede
      // zweite Entscheidung im selben geöffneten Datensatz garantiert stale (409), bis der
      // Prüfer schließt und neu öffnet. Zusätzlich autoritativ nachladen.
      const nextRev = saved?.data?.revision;
      setOpen((o) => (o ? { ...o, review_revision: nextRev ?? (o.review_revision || 0) + 1,
        review_status: saved?.data?.status ?? o.review_status } : o));
      const fresh = await load();
      const authoritative = (fresh || []).find((x) => x.driver_id === open.driver_id);
      if (authoritative) setOpen(authoritative);
      const r = await api.get(`/admin/driver-radar/${open.driver_id}/audit`);
      setAudit(r.data.audit || []);
      setForm((f) => ({ ...f, note: '' }));
    } catch (e) {
      const err = e?.response?.data;
      setMsg({ ok: false, text: err?.error === 'revision_conflict'
        ? 'Inzwischen wurde von anderer Stelle entschieden — bitte neu laden.'
        : err?.error === 'note_required' ? 'Eine Begründung ist Pflicht.'
        : err?.error || 'Speichern fehlgeschlagen' });
    }
  };

  const exportDriver = async () => {
    if (!open) return;
    try {
      const r = await api.get(`/admin/driver-radar/${open.driver_id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `fahrer-radar-${open.driver_id}.json`;
      a.click();
    } catch (e) { setMsg({ ok: false, text: 'Export fehlgeschlagen' }); }
  };

  const purgeDriver = async () => {
    if (!open) return;
    if (!window.confirm('Reviewdaten dieses Fahrers vollständig löschen? Die Prüfhinweise selbst bleiben erhalten.')) return;
    try {
      await api.delete(`/admin/driver-radar/${open.driver_id}`);
      setMsg({ ok: true, text: 'Reviewdaten gelöscht.' });
      setAudit([]); await load();
    } catch (e) { setMsg({ ok: false, text: 'Löschen fehlgeschlagen' }); }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 4px' }}>
        <ShieldCheck size={22} /> Fahrer-Radar – Prüfhinweise
      </h1>

      <div data-testid="radar-contract-banner" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8,
        padding: '12px 14px', margin: '12px 0 20px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: 2, color: '#374151' }} />
        <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
          Diese Liste ist ein <strong>Prüfhinweis, keine Schuldfeststellung</strong>. Es erfolgt keine
          automatische Sperre, keine automatische Offline-Schaltung, kein Matching-Nachteil und keine
          Änderung von Preis, Provision oder Kontostatus. Kundendaten, Adressen und Koordinaten werden
          hier nicht angezeigt — die exakte Position ist ausschließlich über den auditierten
          Standort-Reveal (<code>location_trust.manage</code>) erreichbar, der jeden Abruf protokolliert.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: 13 }}>Zeitraum:&nbsp;
          <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}>
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
        <button onClick={load}>Neu laden</button>
      </div>

      {loading && <p>Lade …</p>}
      {error && <p style={{ color: '#dc2626' }}>{error}</p>}
      {!loading && !error && rows.length === 0 && <p>Keine Prüfhinweise im gewählten Zeitraum.</p>}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
              <th>Fahrer</th><th>Partner</th><th>Betroffene Fahrten</th><th>Zeitraum</th>
              <th>Einstufung</th><th>Prüfstatus</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.driver_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td>{d.driver_name}<div style={{ fontSize: 12, color: '#4b5563' }}>{d.driver_id}</div></td>
                <td>{d.partner_name || '–'}</td>
                <td>{d.distinct_rides}</td>
                <td>{fmt(d.period_from)} – {fmt(d.period_to)}</td>
                <td>
                  <SeverityPill value={d.severity} />
                  {d.earlier_red_hint && (
                    <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4,
                      display: 'flex', alignItems: 'center', gap: 4 }}>
                      <AlertTriangle size={12} /> Hinweis: PIN + Standortbeleg vorhanden
                    </div>
                  )}
                  {d.has_incomplete_legacy && (
                    <div style={{ fontSize: 12, color: '#d97706', marginTop: 2 }}>enthält unvollständige Legacy-Fälle</div>
                  )}
                </td>
                <td>{d.review_status
                  ? <>{(REVIEW_STATUSES.find((s) => s.key === d.review_status) || {}).label || d.review_status}
                      <div style={{ fontSize: 12, color: '#4b5563' }}>{d.review_decided_by} · {fmt(d.review_decided_at)}</div></>
                  : <span style={{ color: '#4b5563' }}>offen</span>}</td>
                <td><button onClick={() => openDriver(d)}><Eye size={14} /> Prüfen</button></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {open && (
        <div style={{ marginTop: 28, border: '1px solid #e5e7eb', borderRadius: 8, padding: 18 }}>
          <h2 style={{ marginTop: 0 }}>{open.driver_name} <span style={{ fontSize: 13, color: '#4b5563' }}>({open.driver_id})</span></h2>

          <h3 style={{ fontSize: 14 }}>Betroffene Fahrten ({open.distinct_rides})</h3>
          <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                <th>Fahrt</th><th>Rückgabegrund</th><th>Ankunft</th><th>Rückgabe</th>
                <th>PIN</th><th>Standortbeleg</th><th>Fahrtausgang</th><th>Datenlage</th>
              </tr>
            </thead>
            <tbody>{(open.cases || []).map((c, i) => <CaseRow key={`${c.ride_id}-${i}`} c={c} />)}</tbody>
          </table></div>

          <p style={{ fontSize: 12, color: '#4b5563', display: 'flex', alignItems: 'center', gap: 6 }}>
            <MapPin size={13} /> Exakte Positionen sind hier bewusst nicht enthalten.
          </p>

          <h3 style={{ fontSize: 14, marginTop: 22 }}>Prüfentscheid</h3>
          {!canManage && <p style={{ color: '#4b5563', fontSize: 13 }}>Nur Lesezugriff — Entscheiden erfordert <code>driver_radar.manage</code>.</p>}
          {canManage && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 620 }}>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="">Status wählen …</option>
                {REVIEW_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <textarea rows={3} placeholder="Begründung (Pflicht)" value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={saveReview} disabled={!form.status || form.note.trim().length < 3}>Entscheid speichern</button>
                <button onClick={exportDriver}><Download size={14} /> Export</button>
                <button onClick={purgeDriver} style={{ color: '#dc2626' }}><Trash2 size={14} /> Reviewdaten löschen</button>
              </div>
              {msg && <p style={{ color: msg.ok ? '#16a34a' : '#dc2626', fontSize: 13 }}>{msg.text}</p>}
            </div>
          )}

          <h3 style={{ fontSize: 14, marginTop: 22 }}>Entscheidungsverlauf (unveränderlich)</h3>
          {audit.length === 0 && <p style={{ fontSize: 13, color: '#4b5563' }}>Noch keine Entscheidung.</p>}
          {audit.length > 0 && (
            <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th>Zeitpunkt</th><th>Von</th><th>Nach</th><th>Begründung</th><th>Entscheider</th><th>Rev.</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id}>
                    <td>{fmt(a.decided_at)}</td><td>{a.from_status || '–'}</td><td>{a.to_status}</td>
                    <td>{a.note}</td><td>{a.decided_by}</td><td>{a.revision}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}

          <button style={{ marginTop: 16 }} onClick={() => { setOpen(null); setAudit([]); }}>Schließen</button>
        </div>
      )}
    </div>
  );
}
